import type {
  LearnerProfile_EducationHistory as EducationHistory,
  LearnerProfile_Occupation as LearnerProfileOccupation,
} from '__generated__/graphql-types-do-not-use';
import _ from 'lodash';

import { getEducationChoicesLearnerProfileV1, skillTypes } from 'bundles/account-profile/common/constants';
import type {
  IndustryInput,
  InputOption,
  OccupationInput,
  PreferredOccupation,
  SkillInput,
} from 'bundles/account-profile/common/types';
import type { OccupationIdToRoleMap } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import type { Industry, Occupation } from 'bundles/naptimejs/resources/profiles.v1';
import type ProfilesV1 from 'bundles/naptimejs/resources/profiles.v1';

import _t from 'i18n!nls/account-profile';

const { USER_GENERATED_SKILL, USER_INTERESTED_SKILL_ID } = skillTypes;

function formatOccupation(value: OccupationInput) {
  if (value.occupationId) {
    return {
      typeName: 'OccupationId',
      definition: {
        id: value.occupationId,
      },
    };
    // Send the object below even if the freeform value is empty string
    // Sending `null` will result in the field not getting updated
  } else {
    return {
      typeName: 'UserGeneratedOccupation',
      definition: {
        name: value.occupationFreeform,
      },
    };
  }
}

function formatSchool(schoolName?: string | null) {
  if (schoolName) {
    return {
      name: schoolName,
    };
  } else {
    return null;
  }
}

function formatIndustry(value: IndustryInput) {
  if (value.industryId) {
    return {
      typeName: 'IndustryId',
      definition: {
        id: value.industryId,
      },
    };
    // Send the object below even if the freeform value is empty string
    // Sending `null` will result in the field not getting updated
  } else {
    return {
      typeName: 'UserGeneratedIndustry',
      definition: {
        name: value.industryFreeform,
      },
    };
  }
}

function formatSkill(skillPair: SkillInput) {
  if (skillPair.id) {
    return {
      typeName: 'UserInterestSkillId',
      definition: {
        skillId: skillPair.id,
      },
    };
  } else {
    return {
      typeName: 'UserGeneratedSkill',
      definition: {
        name: skillPair.name,
      },
    };
  }
}

function formatInterestedCareers(interestedCareers: PreferredOccupation[]) {
  return interestedCareers?.map(
    (career) =>
      career && {
        occupationLevel: Number(career.occupationLevel),
        industry: formatIndustry(career),
        occupation: formatOccupation(career),
      }
  );
}

function formatWebsiteData(data: InputOption[]) {
  const websites: Record<string, string | undefined> = {};
  if (data) {
    data.forEach((site) => {
      if (site) {
        websites[site.id] = site.value;
      }
    });
  }
  return websites;
}

function getWebsitesData(profile: ProfilesV1, requestCountryCode: string): InputOption[] {
  const linkedInData = {
    index: 0,
    id: 'linkedin',
    inputName: 'website_linkedin',
    value: profile.websites?.linkedin,
    visibleLabel: _t('LinkedIn'),
  };
  const facebookData = {
    index: 1,
    id: 'facebook',
    inputName: 'website_facebook',
    value: profile.websites?.facebook,
    visibleLabel: _t('Facebook'),
  };
  const twitterData = {
    index: 2,
    id: 'twitter',
    inputName: 'website_twitter',
    value: profile.websites?.twitter,
    visibleLabel: _t('Twitter'),
  };
  const gPlusData = {
    index: 3,
    id: 'gplus',
    inputName: 'website_gplus',
    value: profile.websites?.gplus,
    visibleLabel: _t('Google+'),
  };
  const githubData = {
    index: 4,
    id: 'github',
    inputName: 'website_github',
    value: profile.websites?.github,
    visibleLabel: _t('Github'),
  };
  const otherData = {
    index: 5,
    id: 'default',
    inputName: 'website',
    value: profile?.websites?.default,
    visibleLabel: _t('Other'),
  };

  let websitesData;
  if (requestCountryCode === 'CN') {
    websitesData = [linkedInData, githubData, otherData];
  } else {
    websitesData = [linkedInData, facebookData, twitterData, gPlusData, githubData, otherData];
  }

  return websitesData;
}

function formatOccupationData(occupation?: Occupation) {
  return {
    occupationFreeform: occupation?.definition?.name || '',
    occupationId: occupation?.definition?.id || '',
  };
}

function formatIndustryData(industry?: Industry) {
  return {
    industryFreeform: industry?.definition?.name || '',
    industryId: industry?.definition?.id || '',
  };
}

function getOccupationData(profile: ProfilesV1) {
  if (!profile || !profile.demographics || !profile.demographics.currentOccupation) {
    return formatOccupationData();
  }

  return formatOccupationData(profile.demographics.currentOccupation);
}

function getIndustryData(profile: ProfilesV1) {
  if (!profile || !profile.demographics || !profile.demographics.currentIndustry) {
    return formatIndustryData();
  }

  return formatIndustryData(profile.demographics.currentIndustry);
}

function getInterestedCareersData(profile: ProfilesV1) {
  if (!profile || !profile.demographics) {
    return [];
  }

  return _.map(profile.demographics.interestedCareers, (career, index) => ({
    id: index,
    occupationLevel: career.occupationLevel,
    ...formatIndustryData(career.industry),
    ...formatOccupationData(career.occupation),
  }));
}

function getLearningProgramsData(profile: ProfilesV1) {
  if (!profile || !profile.demographics) {
    return {};
  }
  return {
    haveTakenOnlineCourse: profile.demographics.haveTakenOnlineCourse,
    learningProgramInterest: profile.demographics.learningProgramInterest,
    haveInterestInTrainingTeam: profile.demographics.haveInterestInTrainingTeam,
  };
}

function getSkillsWantedV2Data(profile?: ProfilesV1): SkillInput[] {
  if (!profile || !profile.demographics) {
    return [];
  }

  return (
    profile.demographics.skillsWantedV2
      ?.map((skill) => {
        if (skill.typeName === USER_GENERATED_SKILL) {
          // @ts-expect-error need to update BE
          return { name: skill.definition?.name } as SkillInput;
        } else if (skill.typeName === USER_INTERESTED_SKILL_ID) {
          // we only return skill id from the skillsData until we migrate to skill API
          // @ts-expect-error need to update BE
          const id = skill.definition?.skillId || skill.definition;
          return { id } as SkillInput;
        } else {
          return null;
        }
      })
      ?.filter((skill): skill is SkillInput => !!skill) || []
  );
}

const sanitizeString = (input?: string, maxLen?: number) => {
  if (typeof input === 'string') {
    const sanitizedString = input.replace(/[^\w .,!@#$%&*()\-_+=~;:\\/?'"{}|[\]]/gi, '');
    return sanitizedString.substr(0, maxLen);
  }
  return '';
};

const getOccupationRoleName = (idToRolesMap: OccupationIdToRoleMap, occupation?: LearnerProfileOccupation | null) => {
  if (occupation?.__typename === 'LearnerProfile_OccupationId') {
    return idToRolesMap.get(occupation.id)?.occupationName ?? '';
  } else if (occupation?.__typename === 'LearnerProfile_UserGeneratedOccupation') {
    return occupation.name;
  } else {
    return '';
  }
};

const getOccupationIdOrName = (occupation?: LearnerProfileOccupation | null) => {
  if (occupation?.__typename === 'LearnerProfile_OccupationId') {
    return occupation.id;
  } else if (occupation?.__typename === 'LearnerProfile_UserGeneratedOccupation') {
    return occupation.name;
  } else {
    return '';
  }
};

const sortEducationHistoryByDegreeTypeDsc = (educationHistory: Array<EducationHistory>) => {
  const degreeTypes = getEducationChoicesLearnerProfileV1();

  if (educationHistory.length === 0) {
    return [];
  } else {
    // I make a copy of the array to avoid sorting in place which sometimes throws an error
    return [...educationHistory].sort((firstEdu, secondEdu) => {
      const firstEduDegreeRank = degreeTypes.find((degreeType) => degreeType.name === firstEdu.degreeType)?.rank || 0;
      const secondEduDegreeRank = degreeTypes.find((degreeType) => degreeType.name === secondEdu.degreeType)?.rank || 0;

      if (firstEduDegreeRank > secondEduDegreeRank) {
        return -1;
      }

      if (firstEduDegreeRank < secondEduDegreeRank) {
        return 1;
      }

      return 0;
    });
  }
};

export {
  getWebsitesData,
  getOccupationData,
  getIndustryData,
  getInterestedCareersData,
  getLearningProgramsData,
  getSkillsWantedV2Data,
  formatIndustry,
  formatSchool,
  formatOccupation,
  formatSkill,
  formatInterestedCareers,
  formatWebsiteData,
  sanitizeString,
  getOccupationRoleName,
  getOccupationIdOrName,
  sortEducationHistoryByDegreeTypeDsc,
};
